import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import Loading from '../../../components/common/Loading';
import Button from '../../../components/common/Button';
import ConfirmationModal from '../../../components/common/ConfirmationModal';

// Import view components
import MobileEditListing from './MobileEditListing';
import DesktopEditListing from './DesktopEditListing';

import ElectronicsForm from '../../../components/store/listing/specialized/ElectronicsForm';
import VehiclesForm from '../../../components/store/listing/specialized/VehiclesForm';
import PropertiesForm from '../../../components/store/listing/specialized/PropertiesForm';
import ServicesForm from '../../../components/store/listing/specialized/ServicesForm';

// Import hooks and utilities
import { 
  getListing, 
  updateListing, 
  getCategories, 
  uploadListingImages, 
  deleteListingImage,
  updateListingStatus,
  IMAGE_BASE_URL
} from '../../../services/api';
import {FiPackage} from 'react-icons/fi'

export default function EditListingScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [listing, setListing] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    price: '',
    condition: 'brand_new',
    listing_type: 'product',
    category_id: '',
    is_negotiable: false,
    status: 'active',
  });
  const [specializedData, setSpecializedData] = useState({});
  const [categories, setCategories] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  
  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusToUpdate, setStatusToUpdate] = useState('');

  // Determine category type for specialized forms
  const CATEGORY_TYPE_MAPPING = {
    // Electronics - specific device subcategories
    12: 'electronics', // Computers & Laptops
    19: 'electronics', // Mobile Phones
    20: 'electronics', // Tablets
    22: 'electronics', // Smartwatches
    13: 'electronics', // TVs & Audio Systems
    14: 'electronics', // Cameras & Drones
    15: 'electronics', // Gaming // consoles
    
    // Services - all service subcategories
    85: 'services', // Repair & Maintenance
    86: 'services', // Home Cleaning & Laundry
    87: 'services', // Pest Control
    88: 'services', // Event Planning & Photography
    89: 'services', // Health & Fitness
    90: 'services', // Education & Tutoring
    91: 'services', // Logistics & Delivery
    92: 'services', // Business & Tech Services
    93: 'services', // Legal & Financial Services
    94: 'services', // Plumbing & Water Treatment
    
    // Vehicles - only complete vehicles
    24: 'vehicles', // Cars
    25: 'vehicles', // Motorcycles & Scooters
    26: 'vehicles', // Trucks & Trailers
    27: 'vehicles', // Buses & Vans
    30: 'vehicles', // Boats & Watercraft
    
    // Properties - all property subcategories
    62: 'properties', // Houses for Sale
    63: 'properties', // Houses for Rent
    64: 'properties', // Land & Plots
    65: 'properties', // Commercial Property
    66: 'properties', // Short Let Apartments
    68: 'properties', // Office & Shop Spaces
    69: 'properties', // Hostels & Student Accommodation
  };

  const getCategoryType = useCallback(() => {
    if (!form.category_id) return null;
    
    try {
      const categoryId = parseInt(form.category_id);
      
      // Check direct mapping
      if (CATEGORY_TYPE_MAPPING[categoryId]) {
        return CATEGORY_TYPE_MAPPING[categoryId];
      }
      
      // Fallback to category name detection
      const category = categories.find(cat => cat.id === categoryId);
      if (!category) return null;

      const categoryName = category.name.toLowerCase();
      
      if (categoryName.includes('phones') || categoryName.includes('laptop') || 
          categoryName.includes('electronic') || categoryName.includes('computer') ||
          categoryName.includes('camera') || categoryName.includes('tablet') ||
          categoryName.includes('gaming') || categoryName.includes('audio')) {
        return 'electronics';
      }
      
      if (categoryName.includes('car') || categoryName.includes('vehicle') || 
          categoryName.includes('motorcycle') || categoryName.includes('bike') ||
          categoryName.includes('boat') || categoryName.includes('truck')) {
        return 'vehicles';
      }
      
      if (categoryName.includes('property') || categoryName.includes('house') || 
          categoryName.includes('apartment') || categoryName.includes('real estate') ||
          categoryName.includes('rent') || categoryName.includes('land')) {
        return 'properties';
      }
      
      if (categoryName.includes('service') || categoryName.includes('cleaning') || 
          categoryName.includes('tutoring') || categoryName.includes('beauty') ||
          categoryName.includes('repair') || categoryName.includes('maintenance') ||
          categoryName.includes('consulting') || categoryName.includes('fitness')) {
        return 'services';
      }
      
      return null;
    } catch (error) {
      // console.error('Error determining category type:', error);
      return null;
    }
  }, [form.category_id, categories]);

  const categoryType = getCategoryType();

  // Condition config
  const conditionConfig = {
    brand_new: { 
      label: 'Brand New', 
      color: 'text-green-800', 
      bgColor: 'bg-green-100',
      borderColor: 'border-green-200'
    },
    like_new: { 
      label: 'Like New', 
      color: 'text-emerald-800', 
      bgColor: 'bg-emerald-100',
      borderColor: 'border-emerald-200'
    },
    fairly_used: { 
      label: 'Fairly Used', 
      color: 'text-amber-800', 
      bgColor: 'bg-amber-100',
      borderColor: 'border-amber-200'
    },
    used: { 
      label: 'Used', 
      color: 'text-yellow-800', 
      bgColor: 'bg-yellow-100',
      borderColor: 'border-yellow-200'
    },
    refurbished: { 
      label: 'Refurbished', 
      color: 'text-purple-800', 
      bgColor: 'bg-purple-100',
      borderColor: 'border-purple-200'
    }
  };

  const conditionOptions = [
    { id: 'brand_new', name: 'Brand New', value: 'brand_new' },
    { id: 'like_new', name: 'Like New', value: 'like_new' },
    { id: 'fairly_used', name: 'Fairly Used', value: 'fairly_used' },
    { id: 'used', name: 'Used', value: 'used' },
    { id: 'refurbished', name: 'Refurbished', value: 'refurbished' },
  ];

  const handleBack = () => {
    navigate(-1);
  };

  // Check if listing is visible to buyers
  const isListingVisibleToBuyers = useCallback(() => {
    return listing?.moderation_status === 'approved' && listing?.status === 'active';
  }, [listing]);

  useEffect(() => {
    fetchListingAndCategories();
  }, [id]);

  const fetchListingAndCategories = async () => {
    try {
      setLoading(true);
      const [listingResponse, categoriesResponse] = await Promise.all([
        getListing(id),
        getCategories()
      ]);

      if (listingResponse.data) {
        const listingData = listingResponse.data;
        
        setListing(listingData);
        setForm({
          title: listingData.title || '',
          description: listingData.description || '',
          price: listingData.price?.toString() || '',
          condition: listingData.condition || 'brand_new',
          listing_type: listingData.listing_type || 'product',
          category_id: listingData.category_id?.toString() || '',
          is_negotiable: Boolean(listingData.is_negotiable),
          status: listingData.status || 'active',
        });
        setExistingImages(listingData.images || []);
        
        // Handle specialized data
        if (listingData.specialized_data) {
          const { _type, listing_id, ...cleanData } = listingData.specialized_data;
          setSpecializedData(cleanData || {});
        } else {
          setSpecializedData({});
        }
      }

      if (categoriesResponse.data) {
        setCategories(categoriesResponse.data);
      }
    } catch (err) {
      // console.error('Error fetching data:', err);
      setError('Failed to load listing data');
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const totalImages = existingImages.length + newImages.length;
    
    if (totalImages >= 10) {
      alert('Maximum images reached. You can only have up to 10 images per listing.');
      return;
    }

    const remainingSlots = 10 - totalImages;
    const selectedFiles = files.slice(0, remainingSlots);

    const newImageFiles = selectedFiles.map(file => ({
      uri: URL.createObjectURL(file),
      file: file
    }));

    setNewImages([...newImages, ...newImageFiles]);
  };

  const removeExistingImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to remove this image?')) return;
    
    try {
      await deleteListingImage(imageId);
      setExistingImages(existingImages.filter(img => img.image_id !== imageId));
    } catch (err) {
      // console.error('Error removing image:', err);
      alert('Failed to remove image');
    }
  };

  const removeNewImage = (index) => {
    const updatedImages = newImages.filter((_, i) => i !== index);
    setNewImages(updatedImages);
  };

  const handleFieldChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSpecializedDataChange = (field, value) => {
    setSpecializedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!form.price || isNaN(parseFloat(form.price)) || parseFloat(form.price) <= 0) {
      setError('Valid price is required');
      return;
    }
    if (!form.category_id) {
      setError('Category is required');
      return;
    }

    setShowStatusModal(true);
  };

  const confirmUpdate = async () => {
    try {
      setSubmitting(true);
      setError('');
      
      const listingData = {
        title: form.title.trim(),
        description: form.description.trim(),
        price: parseFloat(form.price),
        condition: form.condition,
        listing_type: form.listing_type,
        category_id: parseInt(form.category_id),
        is_negotiable: form.is_negotiable,
        status: form.status,
      };

      // Include specialized data
      if (categoryType && Object.keys(specializedData).length > 0) {
        listingData.specialized_data = {
          type: categoryType,
          data: specializedData
        };
      }

      const response = await updateListing(id, listingData);

      if (newImages.length > 0) {
        try {
          await uploadListingImages(id, newImages.map(img => img.file));
        } catch (imageError) {
          // console.error('New image upload failed:', imageError);
          alert('Listing was updated successfully, but there was an issue uploading some new images.');
        }
      }

      alert('Your listing has been updated successfully.');
      navigate(-1);
      
    } catch (err) {
      // console.error('Listing update failed:', err);
      setError('Failed to update listing: ' + (err.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
      setShowStatusModal(false);
    }
  };

  const handleQuickStatusUpdate = (newStatus) => {
    setStatusToUpdate(newStatus);
    setShowStatusModal(true);
  };

  const confirmStatusUpdate = async () => {
    try {
      await updateListingStatus(id, statusToUpdate);
      
      setForm(prev => ({ ...prev, status: statusToUpdate }));
      setListing(prev => prev ? { ...prev, status: statusToUpdate } : null);
      
      alert(`Listing status updated to ${statusToUpdate}.`);
      setShowStatusModal(false);
      setStatusToUpdate('');
      
    } catch (err) {
      // console.error('Status update failed:', err);
      alert('Failed to update listing status');
    }
  };

  const handleDeleteListing = async () => {
    try {
      // You'll need to add a deleteListing function to your api.js
      // await deleteListing(id);
      alert('Listing deleted successfully');
      navigate('/store/listings');
    } catch (err) {
      // console.error('Error deleting listing:', err);
      alert('Failed to delete listing');
    }
  };

  const renderSpecializedForm = () => {
    if (!categoryType) return null;

    const safeFormData = specializedData || {};

    const commonProps = {
      form: safeFormData,
      onFieldChange: handleSpecializedDataChange,
      errors: {},
      submitting,
      categoryId: form.category_id
    };

    switch (categoryType) {
      case 'electronics':
        return <ElectronicsForm {...commonProps} />;
      case 'vehicles':
        return <VehiclesForm {...commonProps} />;
      case 'properties':
        return <PropertiesForm {...commonProps} />;
      case 'services':
        return <ServicesForm {...commonProps} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading size="large" />
        <p className="mt-4 text-gray-600">Loading listing data...</p>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
            <FiPackage className="w-12 h-12 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Listing Not Found</h2>
          <p className="text-gray-600 mb-8">
            We couldn't find the listing you're looking for.
          </p>
          <Button 
            onClick={handleBack}
            variant="primary"
            size="large"
            className="px-8"
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile View */}
      <MobileEditListing
        listing={listing}
        form={form}
        specializedData={specializedData}
        categories={categories}
        existingImages={existingImages}
        newImages={newImages}
        error={error}
        submitting={submitting}
        categoryType={categoryType}
        conditionOptions={conditionOptions}
        conditionConfig={conditionConfig}
        isListingVisibleToBuyers={isListingVisibleToBuyers()}
        onBack={handleBack}
        onSubmit={handleSubmit}
        onFieldChange={handleFieldChange}
        onSpecializedDataChange={handleSpecializedDataChange}
        onImageSelect={handleImageSelect}
        onRemoveExistingImage={removeExistingImage}
        onRemoveNewImage={removeNewImage}
        onQuickStatusUpdate={handleQuickStatusUpdate}
        onDeleteListing={() => setShowDeleteModal(true)}
        renderSpecializedForm={renderSpecializedForm}
      />

      {/* Desktop View */}
      <DesktopEditListing
        listing={listing}
        form={form}
        specializedData={specializedData}
        categories={categories}
        existingImages={existingImages}
        newImages={newImages}
        error={error}
        submitting={submitting}
        categoryType={categoryType}
        conditionOptions={conditionOptions}
        conditionConfig={conditionConfig}
        isListingVisibleToBuyers={isListingVisibleToBuyers()}
        onBack={handleBack}
        onSubmit={handleSubmit}
        onFieldChange={handleFieldChange}
        onSpecializedDataChange={handleSpecializedDataChange}
        onImageSelect={handleImageSelect}
        onRemoveExistingImage={removeExistingImage}
        onRemoveNewImage={removeNewImage}
        onQuickStatusUpdate={handleQuickStatusUpdate}
        onDeleteListing={() => setShowDeleteModal(true)}
        renderSpecializedForm={renderSpecializedForm}
      />

      {/* Modals */}
      <ConfirmationModal
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setStatusToUpdate('');
        }}
        onConfirm={statusToUpdate ? confirmStatusUpdate : confirmUpdate}
        title={statusToUpdate ? `Mark as ${statusToUpdate.charAt(0).toUpperCase() + statusToUpdate.slice(1)}` : "Update Listing"}
        message={statusToUpdate 
          ? `Are you sure you want to mark this listing as ${statusToUpdate}?`
          : "Are you sure you want to update this listing?"
        }
        confirmText={statusToUpdate ? "Update Status" : "Update Listing"}
        cancelText="Cancel"
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteListing}
        title="Delete Listing"
        message="Are you sure you want to delete this listing? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </>
  );
}